import { Injectable } from '@angular/core';
import { Timestamp } from '@ngx-grpc/well-known-types';
import { Observable, map } from 'rxjs';
import { DateTime } from 'luxon';
import { AuthService } from '../../../authentication/service/auth.service';
import { GetUserReportRequest, GetUserReportResponse } from '../../../protos/generated/admin.pb';
import { SmartChargeEndpointClient } from '../../../protos/generated/admin.pbsc';
import {
  ActivityReportBySerial,
  ActivityReportItem,
  ActivityReportTotals
} from '../models/activity-reports.models';

@Injectable({
  providedIn: 'root',
})
export class ActivityReportService {
  constructor(
    private readonly smartChargeClient: SmartChargeEndpointClient,
    private readonly authService: AuthService
  ) { }

  getActivityReportGroupedBySerial(
    userId: number,
    start: DateTime,
    end: DateTime
  ): Observable<ActivityReportBySerial[]> {
    const request = new GetUserReportRequest({
      userId,
      startDate: Timestamp.fromDate(start.toJSDate()),
      endDate: Timestamp.fromDate(end.toJSDate()),
    });
    const metadata = this.authService.getMetadata();

    return this.smartChargeClient
      .getActivityReportByUserId(request, metadata)
      .pipe(map((response) => this.mapResponseToSerialBuckets(response)));
  }

  private mapResponseToSerialBuckets(
    response: GetUserReportResponse
  ): ActivityReportBySerial[] {
    const buckets = new Map<string, ActivityReportBySerial>();
    const ensureBucket = (serialNumber: string): ActivityReportBySerial => {
      if (!buckets.has(serialNumber)) {
        buckets.set(serialNumber, {
          serialNumber,
          items: [],
          automaticTotals: [],
          manualTotals: [],
        });
      }
      return buckets.get(serialNumber)!;
    };

    const safeNumber = (val: any): number => {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };

    const safeNullableNumber = (val: any): number | null => {
      if (val === undefined || val === null) return null;
      if (typeof val === 'number') return Number.isFinite(val) ? val : null;

      const candidate = val?.value ?? val?._value ?? val;
      const n = Number(candidate);
      return Number.isFinite(n) ? n : null;
    };

    // Items
    (response.items ?? []).forEach((raw: any) => {
      const serialNumber: string = raw?.serialNumber ?? 'UNKNOWN';
      const bucket = ensureBucket(serialNumber);
      const item: ActivityReportItem = {
        localDate: String(raw?.localDate ?? ''),
        hour: safeNumber(raw?.hour),
        chargedKwh: safeNumber(raw?.chargedKwh),
        dischargedKwh: safeNumber(raw?.dischargedKwh),
        electricityPrice: safeNumber(raw?.electricityPrice),
        totalPrice: safeNumber(raw?.totalPrice),
        serialNumber,
        solarProductionKwh: safeNumber(raw?.solarProductionKwh),
        chargedFromGridKwh: safeNumber(raw?.chargedFromGridKwh),
        isAutomatic: Boolean(raw?.isAutomatic),
        acCharge: this.getNullableBoolean(raw?.acCharge),
        dischargeLimitPercentage: raw?.dischargeLimitPercentage?.value ?? null,
        chargeLimitPercentage: raw?.chargeLimitPercentage?.value ?? null,
        chargeIsSet: this.getNullableBoolean(raw?.chargeIsSet),
        dischargeIsSet: this.getNullableBoolean(raw?.dischargeIsSet),
        soc: safeNullableNumber(raw?.soc),
        consumptionKwh: raw?.consumptionKwh?.value ?? null,
      };

      bucket.items.push(item);
    });

    // Automatic totals
    (response.automaticTotals ?? []).forEach((raw: any) => {
      const serialNumber: string = raw?.serialNumber ?? 'UNKNOWN';
      const bucket = ensureBucket(serialNumber);

      const totals: ActivityReportTotals = {
        serialNumber,
        totalChargedKwh: safeNumber(raw?.totalChargedKwh),
        totalDischargedKwh: safeNumber(raw?.totalDischargedKwh),
        totalSolarProductionKwh: safeNumber(raw?.totalSolarProductionKwh),
        totalPrice: safeNumber(raw?.totalPrice),
        totalConsumptionKwh: safeNumber(raw?.totalConsumptionKwh),
      };

      bucket.automaticTotals.push(totals);
    });

    // Manual totals
    (response.manualTotals ?? []).forEach((raw: any) => {
      const serialNumber: string = raw?.serialNumber ?? 'UNKNOWN';
      const bucket = ensureBucket(serialNumber);

      const totals: ActivityReportTotals = {
        serialNumber,
        totalChargedKwh: safeNumber(raw?.totalChargedKwh),
        totalDischargedKwh: safeNumber(raw?.totalDischargedKwh),
        totalSolarProductionKwh: safeNumber(raw?.totalSolarProductionKwh),
        totalPrice: safeNumber(raw?.totalPrice),
        totalConsumptionKwh: safeNumber(raw?.totalConsumptionKwh),
      };

      bucket.manualTotals.push(totals);
    });

    // sort by hour
    for (const bucket of buckets.values()) {
      bucket.items.sort((a, b) => a.hour - b.hour);
    }
    return Array.from(buckets.values());
  }

  getNullableBoolean(val: any): boolean | null {
    return (val === undefined || val === null) ? null : Boolean(val?.value);
  }

  getActivityReportForSerial(
    userId: number,
    start: DateTime,
    end: DateTime,
    serialNumber: string
  ): Observable<ActivityReportBySerial | undefined> {
    return this.getActivityReportGroupedBySerial(userId, start, end).pipe(
      map((list) => list.find((x) => x.serialNumber === serialNumber))
    );
  }
}
